import { useRateFieldError } from "../../../../hooks/useRateFieldError";
import { FC } from "react";

import { Box } from "@mui/material";
import { TicketingFormCategory, formatCurrencyAmount, getCurrencyFromCountry } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../../../Context/RateContext";
import { useRateFieldName } from "../../../../hooks/useRateFieldName";
import { useEnableGroupTicket } from "../../../../hooks/useEnableGroupTicket";
import { RateOptionToggleRow } from "../../RateOptionToggleRow";

import { GroupBundleFields } from "./GroupBundleFields";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export const GroupTicketRow: FC = () => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organization } = useCurrentUserContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { formCategory, isEditing, isOnboarding, isRegistration } = useRateContext();
  const { control, setValue, unregister } = useFormContext<StepTicketsFormData>();
  const enableGroupTicket = useEnableGroupTicket();

  const fieldName = useRateFieldName("isGroup");
  const attendeesFieldName = useRateFieldName("attendeesByTicket");
  const isChecked = useWatch({ control, name: fieldName });
  const error = useRateFieldError("attendeesByTicket");

  const isLottery = formCategory === TicketingFormCategory.Lottery;
  const isSweepstakes = isSweepstakesCategory(formCategory);

  const handleToggle = (checked: boolean) => {
    if (checked) {
      enableGroupTicket();
    } else {
      setValue(fieldName, false, { shouldDirty: true });
      unregister(attendeesFieldName, { keepValue: true });
    }
    logAmplitudeEvent(AmplitudeEvents.TicketOptionToggled, {
      option: "group_ticket",
      enabled: checked,
      entry_point: "manual",
      form_type: formCategory,
      is_liveform: true,
      isEditing,
      isOnboarding,
    });
  };

  const labelKey = isLottery
    ? "ticketing.rateOption.groupTicket.lottery"
    : isSweepstakes
      ? "ticketing.rateOption.groupTicket.sweepstakes"
      : isRegistration
        ? "ticketing.rateOption.groupTicket.registration"
        : "ticketing.rateOption.groupTicket";

  const tooltipKey = isLottery
    ? "ticketing.isRateGroupInformation.lottery"
    : isSweepstakes
      ? "ticketing.isRateGroupInformation.sweepstakes"
      : isRegistration
        ? "ticketing.isRateGroupInformation.registration"
        : "ticketing.isRateGroupInformation";

  return (
    <RateOptionToggleRow
      optionKey="groupTicket"
      checked={Boolean(isChecked)}
      onToggle={handleToggle}
      label={t("dashboard", labelKey)}
      disabled={isRegistration}
      disabledTooltip={t("dashboard", "ticketing.rateOption.groupTicket.registrationDisabled")}
      tooltip={t("dashboard", tooltipKey, {
        amount: formatCurrencyAmount({ value: 25, locale, currency: getCurrencyFromCountry(organization?.country) }),
      })}
      inlineConfig={isChecked ? <GroupBundleFields isJustChecked={Boolean(isChecked)} /> : undefined}
      belowContent={
        Boolean(isChecked) && error?.message ? (
          <Box component="span">
            <DashboardTypography component="p" variant="caption" vibe="error-main">
              {error.message}
            </DashboardTypography>
          </Box>
        ) : undefined
      }
    />
  );
};
